import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { EMPTY, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { CatalogInfo, CatalogView } from '@shared/models/shared-catalog.state';
import { SharedCatalogState } from '@shared/state/catalog/shared-catalog.state';
import { SaveCatalogToStorage } from '@shared/state/catalog/shared-catalog.action';
import { catchError, distinctUntilChanged, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { parseQueryStringByDocument, setLocationQueryParams } from '@utils/other-utils';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DOCUMENT, Location } from '@angular/common';
import { DateTime } from 'luxon';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IDestroy, TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { BreakpointObserver } from '@angular/cdk/layout';
import { cloneDeep, uniqWith } from 'lodash';
import { saveAs } from 'file-saver';
import { DynamicCatalogFilesComponent } from '@shared/components/dynamic-catalog-files/dynamic-catalog-files/dynamic-catalog-files.component';
import { HistoryVersionListComponent } from '@shared/components/passport/history-version-list-modal/history-version-list-modal.component';
import { MirSnackbarType } from '@shared/components/snackbar-message/model/snackbar';
import { SnackBarService } from '@shared/components/snackbar-message/services/snackbar.service';
import { CatalogService } from '@shared/services/catalog.service';
import { ReleaseStatus } from '@shared/models/catalog.service';
import { TranslateService } from '@ngx-translate/core';
import { Locale } from '@shared/models/locale';
import { LangChangeEvent } from '@ngx-translate/core/lib/translate.service';
import { StatsMosService } from '@shared/services/stats-mos.service';
import { CatalogItemsDataRouting } from '../../../data/modules/catalog-items-data/models/catalog-items-data-routing';
import { CatalogVersionRelease, FileType, TypeInfo } from '../../../data/models/catalog';
import { DownloadCatalogService } from '../../../data/services/download-catalog.service';
import { CatalogItemService } from '../../../data/modules/catalog-items-data/services/catalog-item.service';
import { CatalogItemsQueryParams } from '../../../data/modules/catalog-items-data/models/catalog-item-component';
import { SetIsDynamic } from '../../../data/modules/catalog-items-data/store/catalog-items.actions';
import { CatalogItemsState } from '../../../data/modules/catalog-items-data/store/catalog-items-state';
import { CatalogState } from '../../../data/store/catalog/catalog.state';
import { GetAllCatalogVersion } from '../../../data/store/catalog/catalog.action';
import { CatalogStateType } from '../../../data/modules/catalog-items-data/models/catalog-item.service';
import { FilesRouteService } from '../../../secondary-pages/files/files-route.service';

@Component({
  selector: 'app-catalog-passport',
  templateUrl: './catalog-passport.component.html',
  styleUrls: ['./catalog-passport.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class CatalogPassportComponent implements OnInit, IDestroy, OnDestroy {
  private _timer: Subscription | null;
  private _modalComponent: MatDialogRef<HistoryVersionListComponent, any>;
  private _catalogId: number;
  private _isMobile: boolean;
  private _catalog: CatalogView;
  public isDynamic: boolean;
  public componentDestroy: () => Subject<any>;
  @Input() public isImpairedVersion = false;
  public showActualVersion = true;
  public tableRoute: string;
  public tableRouteParams: Params;
  public isRuLocale = true;
  public langChange$: Observable<LangChangeEvent>;
  public actualRelease = ReleaseStatus.Actual;
  @Select(SharedCatalogState.catalogInfo) public selectCatalogInfo$: Observable<CatalogInfo>;
  public selectCatalog$: Observable<CatalogView>;
  public currentCatalogVersions$: Observable<CatalogVersionRelease[] | undefined>;
  public currentCatalogFiles$: Observable<TypeInfo[] | undefined>;
  public lastVersion: CatalogVersionRelease | undefined;
  public releaseDate: string | undefined;
  public downloadFiles: (TypeInfo & CatalogVersionRelease)[] = [];
  public allDownloadFiles: (TypeInfo & CatalogVersionRelease)[] = [];
  public versions: CatalogVersionRelease[];
  public uniqueVersions: CatalogVersionRelease[];
  public breakPointObserve$ = this.breakpointObserver.observe(['(max-width: 450px)']).pipe(
    tap(state => {
      this._isMobile = state.breakpoints['(max-width: 450px)'];
    })
  );

  constructor(
    private store: Store,
    private router: Router,
    private location: Location,
    private activeRoute: ActivatedRoute,
    private downloadService: DownloadCatalogService,
    private catalogItemService: CatalogItemService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private snackBar: SnackBarService,
    private breakpointObserver: BreakpointObserver,
    private filesRouteService: FilesRouteService,
    private catalogService: CatalogService,
    @Inject(DOCUMENT) private document: Document,
    private translateService: TranslateService,
    private statsMosService: StatsMosService
  ) {}

  private getStatusTimer(time: number): void {
    if (!this._timer) {
      this._timer = timer(time)
        .pipe(
          tap(() => {
            this.getAllFiles();
          })
        )
        .subscribe(() => {
          if (this._timer) {
            this._timer.unsubscribe();
            this._timer = null;
          }
        });
    }
  }

  private getAllFiles(): void {
    if (this._catalog?.history) {
      const allCatalogId = [this._catalogId, ...this._catalog.history.map(x => x.idPublicCatalog)];

      this.downloadService
        .statusAllFiles(allCatalogId)
        .pipe(take(1), takeUntil(this.componentDestroy()))
        .subscribe(allFiles => {
          if (!Array.isArray(allFiles)) {
            return;
          }
          const files = allFiles.find(c => c.id === this._catalogId)?.files;

          if (files) {
            if (files.some(x => x.status === 'PROCESSING')) {
              this.getStatusTimer(30000);
            }

            const xlsxFiles = files
              .filter(f => (f.status === 'SUCCESS' || f.status === 'ARCHIVE' || f.status === 'PROCESSING') && f.idFile)
              .sort((a, b) => (a.status === 'SUCCESS' && b.status !== 'SUCCESS' ? -1 : 1));
            this.downloadFiles = [];
            this.allDownloadFiles = [];
            xlsxFiles.forEach(file => {
              const fileDate = DateTime.fromFormat(file?.version ?? '', 'yyyy-MM-dd hh:mm:ss').toFormat('yyyyMMddТHHmm');
              const currentVersion = this.versions?.find(v => fileDate === v.releaseDateFull);
              if (currentVersion && file.idFile) {
                this.allDownloadFiles.push({
                  ...currentVersion,
                  ...file
                });
              }
            });

            this.allDownloadFiles = uniqWith(this.allDownloadFiles, (a, b) => a.versionNum === b.versionNum && a.releaseNum === b.releaseNum);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // eslint-disable-next-line array-callback-return,consistent-return
            this.allDownloadFiles = this.allDownloadFiles.sort((x1, x2) => {
              if (x1.versionNum < x2.versionNum) return 1;
              if (x1.versionNum > x2.versionNum) return -1;

              if (x1.releaseNum < x2.releaseNum) return 1;
              if (x1.releaseNum > x2.releaseNum) return -1;
            });
          } else {
            this.allDownloadFiles = this.downloadFiles;
          }

          if (
            this.versions &&
            this.allDownloadFiles[0]?.versionNum === this.versions[0]?.versionNum &&
            this.versions[0]?.releaseNum === this.allDownloadFiles[0]?.releaseNum
          ) {
            this.allDownloadFiles[0].actualVersion = true;
          }
          this.downloadFiles = this.allDownloadFiles.filter(x => x.status !== 'PROCESSING');

          if (this._modalComponent) {
            this._modalComponent.componentInstance.downloadFiles = uniqWith(
              this.versions,
              (a, b) => a.versionNum === b.versionNum && a.releaseNum === b.releaseNum
            ).map(version => {
              const file = this.allDownloadFiles.find(file => file.releaseDateFull === version.releaseDateFull);
              if (file) {
                return {
                  ...file,
                  ...version
                };
              }
              return {
                ...version,
                idFile: 0,
                status: null,
                archiveFileSize: 0,
                format: FileType.CSV
              };
            });
            this._modalComponent.componentInstance.update();
          }
          this.cdr.detectChanges();
        });
    }
  }

  private getStoreParams(): void {
    const params = this.activeRoute.snapshot.queryParams as CatalogItemsQueryParams;

    if (params?.isDynamic && params.isDynamic === 'true') {
      this.isDynamic = true;
      this.store.dispatch(new SetIsDynamic(this.isDynamic, false));
    } else {
      const catalogId = this.store.selectSnapshot(SharedCatalogState.selectCatalog)?.id;
      if (catalogId) {
        const currentCatalogItem = this.store.selectSnapshot(CatalogItemsState.getCatalogItem).call(this, catalogId);

        if (currentCatalogItem) {
          if (currentCatalogItem.isDynamic !== undefined) {
            this.isDynamic = currentCatalogItem.isDynamic;
            setLocationQueryParams({ isDynamic: this.isDynamic }, this.router, this.location);
          }
        }
      }
    }
  }

  private setText(lang: Locale): void {
    if (lang === Locale.RU_RU) {
      this.isRuLocale = true;
    } else {
      this.isRuLocale = false;
    }
  }

  public ngOnInit(): void {
    this.setText(this.translateService.currentLang as Locale);
    this.langChange$ = this.translateService.onLangChange.pipe(
      tap(res => {
        this.setText(res.lang as Locale);
      })
    );
    this.getStoreParams();
    this.selectCatalog$ = this.store.select(SharedCatalogState.selectCatalog).pipe(
      tap(catalog => {
        this._catalog = catalog;
      })
    );
    const selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);

    if (selectCatalog.actualVersionNum && selectCatalog.actualReleaseNum) {
      this.tableRouteParams = {
        isDynamic: selectCatalog.isDynamic,
        version: selectCatalog.actualVersionNum,
        release: selectCatalog.actualReleaseNum
      };
    } else {
      this.showActualVersion = false;
    }

    this.tableRoute = `../${CatalogItemsDataRouting.CatalogItems}`;

    const catalogId = selectCatalog.id;
    this._catalogId = +catalogId;

    const params = parseQueryStringByDocument(this.document);
    if (catalogId && !selectCatalog.isDynamic) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.store.dispatch(new GetAllCatalogVersion(selectCatalog.datasetId, true, params?.access));
      this.currentCatalogVersions$ = this.store.select(CatalogState.getCatalogVersionsInfo).pipe(
        map(filterFn => filterFn(selectCatalog.datasetId)),
        distinctUntilChanged(),
        tap(versions => {
          if (versions && versions.length > 0) {
            this.versions = cloneDeep(versions);
            this.uniqueVersions = [];
            this.uniqueVersions = this.versions.filter(r => r.releaseNum === 1);
            this.releaseDate = this.versions[0].releaseDate;

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // eslint-disable-next-line array-callback-return,consistent-return
            /* this.lastVersion = this.versions.sort((x1, x2) => {
              if (x1.versionNum < x2.versionNum) return 1;
              if (x1.versionNum > x2.versionNum) return -1;

              if (x1.releaseNum > x2.releaseNum) return 1;
              if (x1.releaseNum < x2.releaseNum) return -1;
            })[0]; */

            this.lastVersion = this.versions.find(x => x.releaseStatus === ReleaseStatus.Actual);

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // eslint-disable-next-line array-callback-return,consistent-return,no-unused-expressions
            this.versions.sort((x1, x2) => {
              if (x1.versionNum < x2.versionNum) return 1;
              if (x1.versionNum > x2.versionNum) return -1;

              if (x1.releaseNum < x2.releaseNum) return 1;
              if (x1.releaseNum > x2.releaseNum) return -1;
            })[0];

            if (this.lastVersion && !params?.version && !params?.release) {
              setLocationQueryParams(
                {
                  version: this.lastVersion.versionNum,
                  release: this.lastVersion.releaseNum
                },
                this.router,
                this.location
              );
            }

            this.getAllFiles();
          }
        })
      );
    }
    this.currentCatalogFiles$ = this.store.select(CatalogState.catalogFileInfo).pipe(
      map(filterFn => filterFn(selectCatalog.id)?.files),
      distinctUntilChanged(),
      tap(() => this.getAllFiles())
    );

    this.store.dispatch(new SaveCatalogToStorage());
  }
  public getJsonFileLink(catalogId: number, catalogName: string, releaseDateFull: string | undefined, version: number | undefined): string {
    if (this.isDynamic) {
      return `/opodopendata/${catalogId}/${catalogName}/structure-dynamicdata.json`;
    }
    return `/opodopendata/${catalogId}/${catalogName}/structure-${releaseDateFull}${version ? `(v${version})` : ''}.json`;
  }

  public saveJson(
    catalogId: number,
    catalogName: string,
    releaseDateFull: string | undefined,
    version: number | undefined,
    isDynamic: boolean
  ): void {
    /* if (this.isDynamic) {
      return `/${MainRoutesName.OpenData}/${catalogId}/${catalogName}/structure-dynamicdata.json`;
    } */

    //   return `/${MainRoutesName.OpenData}/${catalogId}/${catalogName}/structure-${releaseDateFull}${version ? `(v${version})` : ''}.json`;

    let releaseDate = '';
    const filename = `structure-${isDynamic ? `dynamicdata` : releaseDateFull}${version ? `(v${version})` : ''}`;
    if (!isDynamic) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      releaseDate = DateTime.fromFormat(filename.split('-')[1].split('.')[0].split('(')[0], 'yyyyMMddТHHmm')
        .plus({ minute: 1 })
        .toFormat('yyyy-MM-dd HH:mm');
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.filesRouteService
      .getCatalogJson(catalogId, catalogName, releaseDate, filename)
      .pipe(
        switchMap(catalogJSON => {
          if (catalogJSON) return of(catalogJSON);

          let req = this.catalogService.getCatalogInfo(catalogId, releaseDate ? { epoch: releaseDate } : {});
          if (isDynamic) {
            req = this.catalogService.getDynamicCatalogInfo(catalogId, releaseDate ? { epoch: releaseDate } : {});
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          return req.pipe(map(response => this.filesRouteService.formatJsonResponse(catalogId, catalogName, response.response[0], filename)));
        }),
        takeUntil(this.componentDestroy())
      )
      .subscribe(res => {
        const jsonData = JSON.stringify(res);
        const blob = new Blob([jsonData], { type: 'application/json' });

        saveAs(blob, `${filename}.json`);
      });
  }

  public convertDate(date: string): string {
    return DateTime.fromFormat(date, 'dd.MM.yyyy').toFormat('yyyyddMM');
  }

  public downloadFile(fileId: number, fileName: string): void {
    this.statsMosService
      .fireEvent({ action: 'setdownload', fileType: 'XLSX', idDataset: this._catalog?.datasetId })
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        error: err => {
          if (err.status !== 200) {
            throw err;
          }
        }
      });
    this.downloadService
      .downloadFile(fileId)
      .pipe(
        catchError(() => {
          this.snackBar.open(MirSnackbarType.Error, [
            {
              translate: true,
              message: 'SNACK_BAR_MESSAGE.EXPORT_ERROR'
            }
          ]);
          return EMPTY;
        }),
        tap((file: ArrayBuffer) => {
          const blob = new Blob([file], {
            type: 'application/zip'
          });

          saveAs(blob, `${fileName}.zip`);
        }),
        switchMap(() =>
          this.catalogItemService.saveCatalogStatistic({
            idOpenCatalog: this._catalogId,
            type: CatalogStateType.DownloadCatalog
          })
        ),
        take(1),
        takeUntil(this.componentDestroy())
      )
      .subscribe();
  }

  public openHistoryList(): void {
    if (this.isDynamic) {
      if (this._catalogId) {
        const dynamicCatalogFilesComponent = this.dialog.open(DynamicCatalogFilesComponent);
        dynamicCatalogFilesComponent.componentInstance.catalogId = this._catalogId;
      }
      return;
    }

    const config = this._isMobile
      ? {
          maxWidth: '100%',
          width: '100%',
          position: {
            bottom: '0px'
          },
          panelClass: 'mobile-bottom-show-up-small-dialog',
          backdropClass: 'backdrop',
          hasBackdrop: true
        }
      : { backdropClass: 'closer-backdrop', panelClass: this.isImpairedVersion ? 'cdk-version-for-visually-impaired' : '' };
    this._modalComponent = this.dialog.open(HistoryVersionListComponent, config);
    this._modalComponent.componentInstance.downloadFiles = uniqWith(
      this.versions,
      (a, b) => a.versionNum === b.versionNum && a.releaseNum === b.releaseNum
    ).map(version => {
      const file = this.allDownloadFiles.find(file => file.releaseDateFull === version.releaseDateFull);
      if (file) {
        return {
          ...file,
          ...version
        };
      }
      return {
        ...version,
        idFile: 0,
        status: null,
        archiveFileSize: 0,
        format: FileType.CSV
      };
    });
    this._modalComponent.componentInstance.catalog = this._catalog;
    this._modalComponent.componentInstance.isImpairedVersion = this.isImpairedVersion;
    this._modalComponent.componentInstance.downloadFile = this.downloadFile.bind(this);
  }

  public ngOnDestroy(): void {
    if (this._timer) {
      this._timer.unsubscribe();
      this._timer = null;
    }
  }
}
